import { useState } from 'react';

import { useBusinessEntity } from '@zenbusiness/application-commons-business-entity';
import { useButtonPressedCallback, useModalDisplayedEvent } from '@zenbusiness/application-commons-clickstream';
import BasicDialog from '@zenbusiness/zui/BasicDialog';
import SharedAsset from '@zenbusiness/zui/SharedAsset';
import SharedLottieAsset from '@zenbusiness/zui/SharedLottieAsset';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';

import { MarkdownRenderer } from '#client/components/MarkdownRenderer';
import useActionTakenUpdate from '#client/hooks/useActionTakenUpdate';
import { useModal } from '#client/hooks/useModal';

/**
 * Check if the URL has Cloudinary transformations
 * @param {string} url - The URL to check
 * @returns {boolean} - True if the URL has Cloudinary transformations, false otherwise
 */
const hasCloudinaryTransforms = (url) =>
  url &&
  (url.includes('c_fill') ||
    url.includes('c_fit') ||
    url.includes('c_scale') ||
    url.includes('c_crop') ||
    url.includes('c_limit') ||
    url.includes('w_') ||
    url.includes('h_') ||
    url.includes('f_auto') ||
    url.includes('q_auto'));

/**
 * Get the styles for the image based on the URL
 * @param {string} url - The URL to get the styles for
 * @returns {object} - The styles for the image
 */
const getImageStyles = (url) => {
  const baseStyles = {
    borderRadius: '12px',
  };

  if (!hasCloudinaryTransforms(url)) {
    return {
      ...baseStyles,
      aspectRatio: '16 / 9',
      objectFit: 'cover',
      objectPosition: 'top center',
    };
  }
  return {
    ...baseStyles,
    width: '16rem',
    display: 'block',
    margin: '0 auto',
  };
};

function PersonalizedModal() {
  const [isModalOpen, setIsModalOpen] = useState(true);
  const { businessEntityUuid } = useBusinessEntity();
  const { data: modal, error, loading } = useModal();

  const interpolatedSubtitle = modal?.subtitle?.replace(/\{businessEntityUuid\}/g, businessEntityUuid);

  const { saveActionTaken, hasError: actionTakenError } = useActionTakenUpdate(modal?.instanceUuid);

  const lottieImage = modal?.image && String(modal?.image).endsWith('.json');

  useModalDisplayedEvent({
    open: !!modal,
    elementName: modal?.inAppKey,
  });

  const handleButtonPressed = useButtonPressedCallback({
    elementName: 'modal_close',
    elementStyle: 'icon',
    elementAsset: modal?.image?.split('/').pop(),
  });

  const handleClose = ({ status }) => {
    // Track All clicks
    const isCta = status === 'cta-clicked';
    if (!isCta) {
      handleButtonPressed();
    }

    // Call mutation and Close the modal
    if (status === 'cta-clicked' || status === 'dismissed') {
      saveActionTaken(status);
      setIsModalOpen(false);
    }
  };

  const handleModalClose = (_, reason) => {
    if (reason !== 'backdropClick') {
      handleClose({ status: 'dismissed' });
    }
  };

  if (error || loading || !modal?.title || actionTakenError) {
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <></>;
  }

  // Handle image rendering based on known cases
  const renderImage = (() => {
    // for lottie images (JSON animation files)
    if (lottieImage) {
      return <SharedLottieAsset src={modal.image} height="20rem" />;
    }

    // for regular images (PNG, JPG, JPEG)
    if (modal.image.includes('.png') || modal.image.includes('.jpg') || modal.image.includes('.jpeg')) {
      return <SharedAsset alt="business-images" src={modal.image} sx={getImageStyles(modal.image)} />;
    }

    // Default case for illustration images (SVG) - most cases
    return (
      <SharedAsset
        alt="business-images"
        src={modal.image}
        sx={{
          width: '10rem',
          maxHeight: '10rem',
          margin: 'auto',
        }}
      />
    );
  })();

  return (
    <BasicDialog
      open={isModalOpen}
      onClose={handleModalClose}
      primaryAction={{
        clickstreamProps: {
          elementName: 'personalize_modal_cta',
          elementText: modal?.ctaLabel,
          destination: modal?.ctaUrl,
        },
        label: modal?.ctaLabel,
        onClick: () => {
          if (modal?.ctaUrl) {
            const target = modal?.ctaType === 'external' ? '_blank' : '_self';
            const link = document.createElement('a');
            link.href = modal.ctaUrl;
            link.target = target;
            link.click();
          }
          handleClose({ status: 'cta-clicked' });
        },
      }}
      subtitle={<MarkdownRenderer>{interpolatedSubtitle}</MarkdownRenderer>}
      title={modal.title}
    >
      <Stack gap={3}>
        {renderImage}

        {modal.caption && <Text>{modal.caption}</Text>}
      </Stack>
    </BasicDialog>
  );
}
export default PersonalizedModal;
